"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { parsePath, navEventFor, track, type PageContext } from "../lib/analytics";

// Rastreador global de GA4. Un único componente que, en cada página:
//  1. Registra el contexto de la página (page_context) con servicio/zona/tipo.
//  2. Captura por delegación todos los clics relevantes (WhatsApp, llamadas y
//     navegación interna) enriquecidos con contexto de origen y destino.
//  3. Mide profundidad de scroll (25/50/75/100%).
//  4. Observa qué secciones ve el usuario ([data-ga-section]).
// Reemplaza al antiguo ConversionTracker. Sustituye instrumentar 500 páginas.

function locationOf(el: HTMLElement | null): string | undefined {
  return el?.closest<HTMLElement>("[data-ga-location]")?.dataset.gaLocation;
}

export default function AnalyticsTracker() {
  const pathname = usePathname();

  useEffect(() => {
    const ctx: PageContext = parsePath(pathname);
    const ctxParams = {
      content_type: ctx.content_type,
      service: ctx.service,
      zone: ctx.zone,
      brand: ctx.brand,
    };

    // 1. Contexto de la página.
    track("page_context", ctxParams);

    // 2. Clics (WhatsApp, llamadas y navegación interna).
    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      const anchor = el?.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href") ?? "";
      const link_location = locationOf(el);

      if (href.includes("wa.me") || href.includes("api.whatsapp.com")) {
        track("whatsapp_click", { ...ctxParams, link_location });
        return;
      }
      if (href.startsWith("tel:")) {
        track("call_click", { ...ctxParams, link_location });
        return;
      }
      // Navegación interna: clasifica el destino por su ruta.
      if (!href.startsWith("/") || href.startsWith("//")) return;
      const nav = navEventFor(parsePath(anchor.pathname));
      if (nav) {
        track(nav.name, {
          ...nav.params,
          source_type: ctx.content_type,
          source_service: ctx.service,
          source_zone: ctx.zone,
          link_location,
        });
      }
    };
    document.addEventListener("click", onClick, { capture: true });

    // 3. Profundidad de scroll (una vez por umbral por página).
    const fired = new Set<number>();
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = ((doc.scrollTop || document.body.scrollTop) / scrollable) * 100;
      for (const m of [25, 50, 75, 100]) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          track("scroll_depth", { percent: m, ...ctxParams });
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // 4. Secciones vistas (elementos con data-ga-section).
    let observer: IntersectionObserver | undefined;
    const sections = document.querySelectorAll<HTMLElement>("[data-ga-section]");
    if (sections.length && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              track("view_section", {
                section: (entry.target as HTMLElement).dataset.gaSection,
                ...ctxParams,
              });
              observer?.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.5 }
      );
      sections.forEach((s) => observer!.observe(s));
    }

    return () => {
      document.removeEventListener("click", onClick, { capture: true });
      window.removeEventListener("scroll", onScroll);
      observer?.disconnect();
    };
  }, [pathname]);

  return null;
}
